import { useEffect, useState, type FormEvent } from 'react'
import FlipButton from '@/components/ui/FlipButton'

/*
  Figma: node 260:3551 (Contact)
  - Page header: "Contact" h1-title (180px alfarn-2 700)
  - Form rows: label 440px (56px Poppins Light) + input (32px Poppins Light), 128px gap
  - 1px black divider under each row
  - Send button: black bg, Poppins Light 40px white uppercase, px-80 py-16, h-72
  - BACK button: Poppins Bold 60px

  Submission: React sets values on the hidden Insites form's web components
  then calls form.requestSubmit(). This fires the submit event which
  InsitesFormProcessor intercepts, running the full platform pipeline:
  AJAX POST, server-side validation, DB record, CRM contact creation,
  workflow + autoresponder emails, and redirect to /thank-you.

  Styling lives in src/styles/pages/contact.css.
*/

interface FormState {
  name: string
  email: string
  phone: string
  message: string
}

type Field = keyof FormState

const FIELD_LABELS: Record<Field, string> = {
  name: 'NAME',
  email: 'EMAIL ADDRESS',
  phone: 'PHONE NUMBER',
  message: 'MESSAGE',
}

const FIELD_PLACEHOLDERS: Record<Field, string> = {
  name: 'Enter full name',
  email: 'Enter email address',
  phone: 'Enter phone number',
  message: 'Enter message',
}

function validate(state: FormState): Partial<Record<Field, string>> {
  const errors: Partial<Record<Field, string>> = {}
  if (!state.name.trim()) errors.name = 'Full name is required.'
  if (!state.email.trim()) {
    errors.email = 'Email is required.'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email.trim())) {
    errors.email = 'Please enter a valid email.'
  }
  if (!state.phone.trim()) errors.phone = 'Phone number is required.'
  if (!state.message.trim()) errors.message = 'Message is required.'
  return errors
}

function splitName(fullName: string): { first_name: string; last_name: string } {
  const trimmed = fullName.trim().replace(/\s+/g, ' ')
  if (!trimmed) return { first_name: '', last_name: '' }
  const parts = trimmed.split(' ')
  if (parts.length === 1) return { first_name: parts[0], last_name: parts[0] }
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') }
}

/**
 * Format phone for the Insites phone field which expects "+CC NUMBER"
 * (country code + space + number). The IIA admin splits on space to
 * display country code and number separately.
 * Default country code: +61 (Australia — MIG is Australian).
 */
function formatPhone(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''

  // Already formatted: "+63 9983910143"
  if (trimmed.startsWith('+') && trimmed.includes(' ')) return trimmed

  // Has country code but no space: "+639983910143" → "+63 9983910143"
  // Default to 2-digit country code (covers +61 AU, +63 PH, +44 UK, etc.)
  // Exception: +1 (US/Canada) is 1-digit
  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1)
    const codeLen = digits.startsWith('1') && digits.length >= 11 ? 1 : 2
    const code = '+' + digits.slice(0, codeLen)
    const rest = digits.slice(codeLen)
    if (rest.length > 0) return `${code} ${rest}`
    return trimmed
  }

  // Plain number: "0450457927" → "+61 0450457927"
  return `+61 ${trimmed}`
}

/**
 * Create a clean native <form> with only our hidden inputs and submit it.
 * We read authenticity_token, form_id, and the action URL from the hidden
 * Insites form, then build a brand-new form element — this completely
 * avoids web component ElementInternals that can inject "undefined" values.
 */
function submitContactForm(values: FormState): boolean {
  const mount = document.getElementById('insites-form-mount')
  if (!mount) return false

  const srcForm = mount.querySelector('form') as HTMLFormElement | null
  if (!srcForm) return false

  const token =
    (srcForm.querySelector('input[name="authenticity_token"]') as HTMLInputElement)?.value ?? ''
  const formId =
    (srcForm.querySelector('input[name="form_id"]') as HTMLInputElement)?.value ?? ''

  if (!token || !formId) return false

  const { first_name, last_name } = splitName(values.name)

  // Build a brand-new form — no web components, no ElementInternals leaking
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = srcForm.action
  form.acceptCharset = 'UTF-8'
  form.style.display = 'none'

  const addField = (name: string, value: string) => {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.appendChild(input)
  }

  // Platform fields
  addField('utf8', '✓')
  addField('authenticity_token', token)
  addField('form_id', formId)

  // Contact form fields
  addField('form[properties_attributes][first_name]', first_name)
  addField('form[properties_attributes][last_name]', last_name)
  addField('form[properties_attributes][email]', values.email.trim())
  addField('form[properties_attributes][mobile_phone]', formatPhone(values.phone))
  addField('form[properties_attributes][message]', values.message.trim())

  document.body.appendChild(form)
  form.submit()
  return true
}

export default function Contact() {
  const [visible, setVisible] = useState(false)
  const [state, setState] = useState<FormState>({ name: '', email: '', phone: '', message: '' })
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const handleChange =
    (field: Field) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setState((prev) => ({ ...prev, [field]: e.target.value }))
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev }
          delete next[field]
          return next
        })
      }
    }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const fieldErrors = validate(state)
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) return

    setSubmitting(true)
    setSubmitError(null)

    const submitted = submitContactForm(state)
    if (!submitted) {
      setSubmitError('Form system not ready. Please refresh the page and try again.')
      setSubmitting(false)
    }
    // InsitesFormProcessor handles the rest (AJAX, emails, CRM, redirect to /thank-you)
  }

  return (
    <section className="mig-page-section">
      <div className="mig-page-title-row" data-visible={visible}>
        <h1 className="mig-contact-page-title">Contact</h1>
      </div>

      <form onSubmit={handleSubmit} noValidate className="mig-contact-form-wrapper">
        <div className="mig-contact-form-fields">
          {(Object.keys(FIELD_LABELS) as Field[]).map((field) => (
            <ContactFieldRow
              key={field}
              field={field}
              label={FIELD_LABELS[field]}
              placeholder={FIELD_PLACEHOLDERS[field]}
              value={state[field]}
              error={errors[field]}
              onChange={handleChange(field)}
              disabled={submitting}
            />
          ))}
        </div>

        <div className="mig-contact-submit-row" data-has-error={!!submitError}>
          {submitError && (
            <p className="mig-contact-submit-error" role="alert">{submitError}</p>
          )}
          <FlipButton
            type="submit"
            disabled={submitting}
            className="mig-contact-submit-btn"
            data-pending={submitting}
          >
            {submitting ? 'Sending...' : 'Send'}
          </FlipButton>
        </div>

        <div className="mig-page-back-row">
          <a href="/" className="mig-contact-back-button">BACK</a>
          <div className="mig-divider-line" />
        </div>
      </form>
    </section>
  )
}

function ContactFieldRow({
  field,
  label,
  placeholder,
  value,
  error,
  onChange,
  disabled,
}: {
  field: Field
  label: string
  placeholder: string
  value: string
  error?: string
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  disabled: boolean
}) {
  const isMessage = field === 'message'
  const commonProps = {
    name: field,
    placeholder,
    value,
    onChange,
    disabled,
    className: 'mig-contact-input',
    'aria-invalid': !!error,
    'aria-describedby': error ? `${field}-error` : undefined,
  }

  return (
    <div className="mig-contact-field-row">
      <div className="mig-contact-field-inner" data-multiline={isMessage}>
        <label htmlFor={`contact-${field}`} className="mig-contact-field-label">
          {label}
        </label>
        {isMessage ? (
          <textarea id={`contact-${field}`} rows={3} {...commonProps} />
        ) : (
          <input
            id={`contact-${field}`}
            type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
            {...commonProps}
          />
        )}
      </div>
      <div className="mig-divider-line" />
      {error && (
        <p id={`${field}-error`} role="alert" className="mig-contact-field-error">
          {error}
        </p>
      )}
    </div>
  )
}
